// Client wrapper for the recruiter risk-flags projection (served same-origin at
// /api/recruiter/*). Mirrors lib/liveVouchApi.ts: a thin fetch that forwards the
// PocketBase session token so the sidecar can authorize the recruiter.
//
// Why a separate fetch instead of riding the realtime `candidates` feed: the
// `candidates.riskFlags` field is HIDDEN (migration 1700000260) so it never reaches
// any PocketBase client — including the candidate it's about (the collection is
// readable by any authed record). This endpoint is the vetted, recruiter-only way
// to read it. Flags are computed at verify time and change rarely, so the store
// fetches them once on load and merges them onto the candidate rows.

import type { RiskFlag } from './types'
import { onAuthExpired } from './pb'

const BASE = '/api/recruiter'

export interface RiskFlagsResult {
  ok: true
  flags: Record<string, RiskFlag[]> // candidateId → flags
}

/** Recruiter+: load the { candidateId → riskFlags[] } map for the pool. */
export async function fetchRiskFlags(pbToken: string): Promise<Record<string, RiskFlag[]>> {
  const res = await fetch(`${BASE}/risk-flags`, {
    headers: { ...(pbToken ? { 'X-PB-Token': pbToken } : {}) },
  })
  const data = (await res.json().catch(() => ({}))) as Partial<RiskFlagsResult> & { error?: string }
  if (res.status === 401) onAuthExpired()
  if (!res.ok) throw new Error(data?.error || 'Could not load risk flags.')
  return data.flags || {}
}
